import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Check, X, Users, Briefcase, Cog, Fuel, Calendar, Shield, ArrowRight } from 'lucide-react';
import BookingSystem from '../components/BookingSystem';
import { GoogleReviews, FAQ } from '../components/SharedSections';
import { translations } from '../mockData';
import { VITO, vitoFaq } from '../data/fleet';
import { PHONE_NUMBER, PHONE_NUMBER_DISPLAY, trackPhoneClick, trackWhatsAppClick } from '../utils/tracking';

const Section = ({ id, eyebrow, title, children, className = '' }) => (
  <section id={id} className={`py-14 sm:py-20 px-4 sm:px-6 lg:px-12 ${className}`}>
    <div className="container mx-auto max-w-5xl">
      {eyebrow && <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">{eyebrow}</p>}
      {title && <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0D1B3E] mb-8">{title}</h2>}
      {children}
    </div>
  </section>
);

const VitoPage = ({ language }) => {
  const isRo = language === 'ro';
  const t = translations[language];
  const L = (ro, en) => (isRo ? ro : en);
  const prefix = isRo ? '' : '/en';

  const facts = [
    [L('Model', 'Model'), `${VITO.name}, ${VITO.year}`],
    [L('Locuri', 'Seats'), VITO.seats],
    [L('Bagaje', 'Luggage'), L(`${VITO.luggage} valize mari, cu toate locurile ocupate`, `${VITO.luggage} large suitcases with all seats in use`)],
    [L('Transmisie', 'Transmission'), VITO.transmission[language]],
    [L('Combustibil', 'Fuel'), VITO.fuel[language]],
    [L('Preț', 'Price'), L(`${VITO.pricePerDay} €/zi`, `€${VITO.pricePerDay}/day`)],
    [L('Garanție', 'Deposit'), L(`${VITO.deposit} €`, `€${VITO.deposit}`)],
    [L('Opțiune SCDW / garanție zero', 'SCDW / zero-deposit option'), L(`${VITO.scdwPerDay} € pe zi`, `€${VITO.scdwPerDay} per day`)],
    [L('Permis necesar', 'Licence needed'), L(`Categoria ${VITO.licence}; vârsta minimă ${VITO.minAge} ani; permis obținut de ${VITO.licenceYears} ani`, `Category ${VITO.licence}; minimum age ${VITO.minAge}; licence held for ${VITO.licenceYears} years`)],
    [L('Kilometri nelimitați', 'Unlimited km'), L('Da', 'Yes')],
    [L('Livrare gratuită în Brașov și la Aeroportul Brașov-Ghimbav', 'Free delivery in Brașov and at Brașov-Ghimbav Airport'), L('Da', 'Yes')],
    [L('Ieșire din țară', 'Cross-border'), L(`Permisă cu aprobare prealabilă, taxă ${VITO.crossBorderFee} €`, `Allowed with prior approval, €${VITO.crossBorderFee} fee`)],
    [L('Anvelope de iarnă', 'Winter tyres'), L('Incluse', 'Included')],
    [L('Scaune pentru copii', 'Child seats'), L('Gratuite', 'Free of charge')],
    [L('Disponibil și cu șofer', 'Also available with a driver'), L('Da, ca transfer', 'Yes, as a transfer')],
  ];

  const audiences = [
    L('Familii care au nevoie de loc pentru toți și pentru bagaje.', 'Families who need room for everyone and their luggage.'),
    L('Grupuri care merg la schi în Poiana Brașov, cu anvelope de iarnă incluse.', 'Groups going skiing in Poiana Brașov, with winter tyres included.'),
    L('Grupuri care sosesc la Aeroportul Brașov-Ghimbav și preiau mașina gratuit acolo.', 'Groups arriving at Brașov-Ghimbav Airport who pick the car up there free of charge.'),
    L('Nunți și evenimente la care trebuie să ajungă împreună până la 8 persoane.', 'Weddings and events where up to 8 people need to travel together.'),
    L('Echipe de companie în deplasare, cu un singur vehicul de 7+1 locuri.', 'Company teams on the move, in a single 7+1 seat vehicle.'),
  ];

  const included = [
    L('Kilometri nelimitați', 'Unlimited kilometres'),
    L('Livrare gratuită în Brașov și la Aeroportul Brașov-Ghimbav', 'Free delivery in Brașov and at Brașov-Ghimbav Airport'),
    L('Anvelope de iarnă', 'Winter tyres'),
    L('Scaune pentru copii, gratuit', 'Child seats, free of charge'),
    L('Cutie automată, motor diesel', 'Automatic gearbox, diesel engine'),
  ];
  const notIncluded = [
    L(`Garanția de ${VITO.deposit} € (se poate înlocui cu opțiunea SCDW, ${VITO.scdwPerDay} €/zi)`, `The €${VITO.deposit} deposit (can be replaced by the SCDW option, €${VITO.scdwPerDay}/day)`),
    L(`Taxa de ${VITO.crossBorderFee} € pentru ieșirea din țară (doar cu aprobare prealabilă)`, `The €${VITO.crossBorderFee} cross-border fee (only with prior approval)`),
    L('Șoferul – pentru varianta cu șofer vezi transferurile', 'A driver – for the chauffeured option see transfers'),
  ];

  const handlePhone = () => { trackPhoneClick(); window.location.href = `tel:${PHONE_NUMBER}`; };
  const handleWhatsApp = () => { trackWhatsAppClick(); window.open('https://wa.me/40726375166', '_blank'); };

  return (
    <>
      <section className="pt-28 sm:pt-32 pb-12 px-4 sm:px-6 lg:px-12 bg-white" data-testid="vito-hero">
        <div className="container mx-auto max-w-5xl">
          <nav aria-label="Breadcrumb" className="font-body text-sm text-gray-500 mb-6" data-testid="vito-breadcrumb">
            <Link to={prefix || '/'} className="hover:text-[#10B981]">{L('Acasă', 'Home')}</Link>
            <span className="mx-2">/</span>
            <Link to={`${prefix}/rent-a-car`} className="hover:text-[#10B981]" data-testid="vito-link-rentals">{L('Închirieri Auto', 'Car Rentals')}</Link>
            <span className="mx-2">/</span>
            <span className="text-[#0D1B3E]">{L('Minivan Mercedes Vito', 'Mercedes Vito Minivan')}</span>
          </nav>
          <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">{L('Închiriere fără șofer', 'Self-drive rental')}</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0D1B3E] leading-tight mb-6" data-testid="vito-title">
            {L('Închiriere Minivan Mercedes Vito în Brașov – 7+1 locuri', 'Mercedes Vito Minivan Rental in Brașov – 7+1 Seats')}
          </h1>
          <p className="font-body text-base sm:text-lg text-gray-600 leading-relaxed max-w-3xl" data-testid="vito-intro">
            {isRo
              ? 'Mercedes-Benz Vito Tourer (2018) este un microbuz cu 7+1 locuri, cutie automată și motor diesel, pe care îl închiriezi fără șofer cu 80 €/zi. Încap 7 valize mari chiar și cu toate locurile ocupate. Prețul include kilometri nelimitați, anvelope de iarnă și scaune pentru copii gratuite. Îl preiei gratuit în Brașov sau la Aeroportul Brașov-Ghimbav. Rezervi din formularul de mai jos, la telefon sau pe WhatsApp, iar dacă preferi o mașină de 7+1 locuri cu șofer, ți-o oferim ca transfer.'
              : 'The Mercedes-Benz Vito Tourer (2018) is an automatic, diesel 7+1 seat minivan you can rent self-drive for €80 per day. It carries 7 large suitcases even with every seat taken. The price includes unlimited kilometres, winter tyres and free child seats. Pick it up free of charge in Brașov or at Brașov-Ghimbav Airport. Book through the form below, by phone or on WhatsApp – and if you would rather have it with a driver, we offer it as a transfer.'}
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <a href="#booking" className="inline-flex items-center gap-2 bg-[#10B981] hover:bg-[#0ea371] text-white font-semibold px-6 py-3 rounded transition-colors" data-testid="vito-cta-book">
              {L('Rezervă Vito', 'Book the Vito')} <ArrowRight size={18} />
            </a>
            <button onClick={handlePhone} className="inline-flex items-center gap-2 border border-[#0D1B3E] text-[#0D1B3E] font-semibold px-6 py-3 rounded hover:bg-[#0D1B3E] hover:text-white transition-colors" data-testid="vito-cta-phone">
              <Phone size={18} /> {PHONE_NUMBER_DISPLAY}
            </button>
            <button onClick={handleWhatsApp} className="inline-flex items-center gap-2 border border-[#25D366] text-[#128C7E] font-semibold px-6 py-3 rounded hover:bg-[#25D366] hover:text-white transition-colors" data-testid="vito-cta-whatsapp">
              <MessageCircle size={18} /> WhatsApp
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
            {[[Users, VITO.seats, L('locuri', 'seats')], [Briefcase, VITO.luggage, L('valize mari', 'large suitcases')], [Cog, VITO.transmission[language], L('transmisie', 'transmission')], [Fuel, VITO.fuel[language], L('combustibil', 'fuel')]].map(([Icon, v, lbl], i) => (
              <div key={i} className="rounded-xl border border-gray-200 p-4 flex items-center gap-3" data-testid={`vito-quick-fact-${i}`}>
                <Icon size={20} className="text-[#10B981] flex-shrink-0" />
                <div><p className="font-display font-bold text-[#0D1B3E]">{v}</p><p className="text-xs text-gray-500">{lbl}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Section id="galerie" eyebrow={L('Galerie foto', 'Photo gallery')} title={L('Mercedes-Benz Vito Tourer 2018', 'Mercedes-Benz Vito Tourer 2018')} className="bg-[#F7F8FA]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="vito-gallery">
          {VITO.images.map((img, i) => (
            <figure key={img.src} className="rounded-xl overflow-hidden bg-gray-100">
              <img src={img.src} alt={isRo ? img.altRo : img.altEn} loading="lazy" decoding="async" width="1536" height="1024" className="w-full h-64 sm:h-80 object-cover" data-testid={`vito-gallery-img-${i}`} />
            </figure>
          ))}
        </div>
      </Section>

      <Section id="detalii" eyebrow={L('Fișa tehnică', 'Fact sheet')} title={L('Toate detaliile, pe scurt', 'All the details at a glance')}>
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-left font-body text-sm sm:text-base" data-testid="vito-fact-table">
            <tbody>
              {facts.map(([k, v], i) => (
                <tr key={k} className={i % 2 ? 'bg-white' : 'bg-[#F7F8FA]'}>
                  <th scope="row" className="px-4 sm:px-6 py-3 font-semibold text-[#0D1B3E] w-1/2 align-top">{k}</th>
                  <td className="px-4 sm:px-6 py-3 text-gray-700">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="pentru-cine" eyebrow={L('Pentru cine', 'Who it\'s for')} title={L('Când are sens un minivan de 7+1 locuri', 'When a 7+1 seat minivan makes sense')} className="bg-[#F7F8FA]">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="vito-audiences">
          {audiences.map((a, i) => (
            <li key={i} className="flex items-start gap-3 rounded-xl bg-white border border-gray-200 p-5 font-body text-gray-700">
              <Check size={18} className="text-[#10B981] mt-1 flex-shrink-0" /> <span>{a}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="cu-sau-fara-sofer" eyebrow={L('Două variante', 'Two options')} title={L('Fără șofer sau cu șofer?', 'Self-drive or with a driver?')}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-xl border-2 border-[#10B981] p-6" data-testid="vito-self-drive">
            <h3 className="font-display text-xl font-bold text-[#0D1B3E] mb-3">{L('Fără șofer (închiriere)', 'Self-drive (rental)')}</h3>
            <ul className="space-y-2 font-body text-gray-700 text-sm sm:text-base">
              <li>{L(`${VITO.pricePerDay} €/zi, kilometri nelimitați`, `€${VITO.pricePerDay}/day, unlimited kilometres`)}</li>
              <li>{L(`Permis categoria B, minimum ${VITO.minAge} ani, permis de ${VITO.licenceYears} ani`, `Category B licence, minimum age ${VITO.minAge}, licence held ${VITO.licenceYears} years`)}</li>
              <li>{L(`Garanție ${VITO.deposit} € sau SCDW ${VITO.scdwPerDay} €/zi`, `€${VITO.deposit} deposit or SCDW €${VITO.scdwPerDay}/day`)}</li>
              <li>{L('Preluare gratuită în Brașov sau la aeroport', 'Free pick-up in Brașov or at the airport')}</li>
            </ul>
            <a href="#booking" className="inline-flex items-center gap-2 mt-5 text-[#10B981] font-semibold hover:underline">{L('Rezervă fără șofer', 'Book self-drive')} <ArrowRight size={16} /></a>
          </div>
          <div className="rounded-xl border border-gray-200 p-6" data-testid="vito-with-driver">
            <h3 className="font-display text-xl font-bold text-[#0D1B3E] mb-3">{L('Cu șofer (transfer)', 'With a driver (transfer)')}</h3>
            <ul className="space-y-2 font-body text-gray-700 text-sm sm:text-base">
              <li>{L('Același Mercedes Vito 7+1, condus de șoferul nostru', 'The same 7+1 Mercedes Vito, driven by our driver')}</li>
              <li>{L('Oferit ca transfer, cu preț pe cursă', 'Offered as a transfer, priced per trip')}</li>
              <li>{L('Fără permis, garanție sau condiții de vârstă pentru tine', 'No licence, deposit or age conditions for you')}</li>
            </ul>
            <Link to={`${prefix}/transfers`} className="inline-flex items-center gap-2 mt-5 text-[#0D1B3E] font-semibold hover:text-[#10B981]" data-testid="vito-link-transfers">{L('Vezi transferurile', 'See transfers')} <ArrowRight size={16} /></Link>
          </div>
        </div>
      </Section>

      <Section id="inclus" eyebrow={L('Transparență', 'Transparency')} title={L('Ce este inclus și ce nu', 'What\'s included and what isn\'t')} className="bg-[#F7F8FA]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ul className="rounded-xl bg-white border border-gray-200 p-6 space-y-3" data-testid="vito-included">
            <li className="font-display font-bold text-[#0D1B3E] mb-2">{L('Inclus', 'Included')}</li>
            {included.map((x) => <li key={x} className="flex items-start gap-3 font-body text-gray-700"><Check size={18} className="text-[#10B981] mt-0.5 flex-shrink-0" />{x}</li>)}
          </ul>
          <ul className="rounded-xl bg-white border border-gray-200 p-6 space-y-3" data-testid="vito-not-included">
            <li className="font-display font-bold text-[#0D1B3E] mb-2">{L('Nu este inclus', 'Not included')}</li>
            {notIncluded.map((x) => <li key={x} className="flex items-start gap-3 font-body text-gray-700"><X size={18} className="text-gray-400 mt-0.5 flex-shrink-0" />{x}</li>)}
          </ul>
        </div>
      </Section>

      <FAQ isRo={isRo} items={vitoFaq} />

      <BookingSystem t={t} preselectedCar={{ id: VITO.id }} language={language} />

      <section className="py-10 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="container mx-auto max-w-5xl flex flex-wrap items-center gap-4 justify-between rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 font-body text-[#0D1B3E]"><Calendar size={20} className="text-[#10B981]" /><span>{L('Preferi să rezervi direct?', 'Prefer to book directly?')}</span></div>
          <div className="flex flex-wrap gap-3">
            <button onClick={handlePhone} className="inline-flex items-center gap-2 bg-[#0D1B3E] text-white font-semibold px-5 py-3 rounded hover:bg-[#1A3A6B] transition-colors" data-testid="vito-bottom-phone"><Phone size={18} /> {PHONE_NUMBER_DISPLAY}</button>
            <button onClick={handleWhatsApp} className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-5 py-3 rounded hover:bg-[#1ebe5d] transition-colors" data-testid="vito-bottom-whatsapp"><MessageCircle size={18} /> WhatsApp</button>
          </div>
        </div>
      </section>

      <GoogleReviews isRo={isRo} />

      <section className="py-10 px-4 sm:px-6 lg:px-12 bg-[#F7F8FA]">
        <div className="container mx-auto max-w-5xl flex flex-wrap gap-6 font-body text-sm">
          <Link to={`${prefix}/rent-a-car`} className="inline-flex items-center gap-2 text-[#0D1B3E] hover:text-[#10B981]" data-testid="vito-footer-link-rentals"><Shield size={16} className="text-[#10B981]" />{L('Vezi toată flota de închirieri', 'See the full rental fleet')}</Link>
          <Link to={`${prefix}/transfers`} className="inline-flex items-center gap-2 text-[#0D1B3E] hover:text-[#10B981]" data-testid="vito-footer-link-transfers"><ArrowRight size={16} className="text-[#10B981]" />{L('Transferuri cu șofer', 'Chauffeured transfers')}</Link>
        </div>
      </section>
    </>
  );
};

export default VitoPage;
